import { loadEmscriptenFactory } from "./emscripten-loader";
import type { MotionKind, ChangeIndicator } from "./history-viewer-types";
import { encodeIndex, decodeIndex } from "./history-viewer-pure";

// Re-export so existing import sites that pull these from the bridge
// keep working without touching every call site. New code should import
// from `./history-viewer-types` directly.
export type { MotionKind, ChangeIndicator };

interface HistoryViewerWasmModule {
  readonly _malloc: (size: number) => number;
  readonly _free: (ptr: number) => void;
  readonly HEAPU32: Uint32Array;
  readonly _history_transition_window: (
    positionMilli: number,
    commitCount: number,
    outPtr: number,
  ) => void;
  readonly _history_fill_lcs_table: (
    fromPtr: number,
    fromLen: number,
    toPtr: number,
    toLen: number,
    dpPtr: number,
    dpLen: number,
  ) => void;
  readonly _history_motion_step: (
    fromPtr: number,
    fromLen: number,
    toPtr: number,
    toLen: number,
    dpPtr: number,
    dpLen: number,
    statePtr: number,
    outPtr: number,
  ) => number;
  readonly _history_transition_stage_line_count: (
    oldLineCount: number,
    newLineCount: number,
    motionPtr: number,
    motionCount: number,
  ) => number;
  readonly _history_transition_viewport_indicators: (
    scrollTop: number,
    clientHeight: number,
    rowHeight: number,
    motionPtr: number,
    motionCount: number,
  ) => number;
}

interface HistoryViewerWasmMotionLine {
  kind: MotionKind;
  fromIndex: number | null;
  toIndex: number | null;
  order: number;
  staggerIndex: number;
}

interface HistoryViewerWasmTransitionWindow {
  lowerIndex: number;
  upperIndex: number;
  localProgress: number;
}

interface HistoryViewerWasmViewportIndicators {
  top: ChangeIndicator;
  bottom: ChangeIndicator;
}

const HISTORY_FACTORY = "createHistoryViewer";
const HISTORY_SCRIPT_URL = "/assets/wasm/history-viewer.js";
const MOTION_STRIDE = 5;
const KIND_UNCHANGED = 0;
const KIND_ADDED = 1;
const KIND_REMOVED = 2;

let historyViewerModulePromise: Promise<HistoryViewerWasmModule | null> | null =
  null;
let historyViewerModule: HistoryViewerWasmModule | null = null;
let historyViewerModuleOverride: HistoryViewerWasmModule | null | undefined;

const decodeKind = (kind: number): MotionKind => {
  switch (kind) {
    case KIND_ADDED:
      return "added";
    case KIND_REMOVED:
      return "removed";
    default:
      return "unchanged";
  }
};

// decodeIndex/encodeIndex live in history-viewer-pure.ts so the WASM
// ABI codec can be property-tested without loading emscripten.

const decodeIndicator = (value: number): ChangeIndicator => {
  switch (value) {
    case KIND_ADDED + 1:
      return "added";
    case KIND_REMOVED + 1:
      return "removed";
    default:
      return null;
  }
};

const internLineIds = (
  fromLines: readonly string[],
  toLines: readonly string[],
): { fromIds: Uint32Array; toIds: Uint32Array } => {
  const ids = new Map<string, number>();
  let nextId = 1;

  const intern = (line: string): number => {
    const existing = ids.get(line);
    if (typeof existing === "number") return existing;
    const created = nextId;
    ids.set(line, created);
    nextId += 1;
    return created;
  };

  return {
    fromIds: Uint32Array.from(fromLines, intern),
    toIds: Uint32Array.from(toLines, intern),
  };
};

const allocU32 = (
  wasm: HistoryViewerWasmModule,
  values: Uint32Array,
): { ptr: number; offset: number } => {
  const ptr = wasm._malloc(values.length * 4);
  const offset = ptr >>> 2;
  wasm.HEAPU32.set(values, offset);
  return { ptr, offset };
};

const primeHistoryViewerWasm =
  async (): Promise<HistoryViewerWasmModule | null> => {
    if (historyViewerModuleOverride !== undefined)
      return historyViewerModuleOverride;
    if (historyViewerModule) return historyViewerModule;
    if (historyViewerModulePromise) return historyViewerModulePromise;

    historyViewerModulePromise = (async () => {
      try {
        const factory = await loadEmscriptenFactory<HistoryViewerWasmModule>(
          HISTORY_FACTORY,
          HISTORY_SCRIPT_URL,
        );

        historyViewerModule = await factory({
          locateFile: (path: string) => `/assets/wasm/${path}`,
        });
        return historyViewerModule;
      } catch {
        return null;
      }
    })();

    return historyViewerModulePromise;
  };

export const setHistoryViewerWasmModuleForTest = (
  module: HistoryViewerWasmModule | null | undefined,
): void => {
  historyViewerModuleOverride = module;
  historyViewerModule = module ?? null;
  historyViewerModulePromise = null;
};

const getReadyHistoryViewerModule = (): HistoryViewerWasmModule | null => {
  if (historyViewerModuleOverride !== undefined)
    return historyViewerModuleOverride;
  return historyViewerModule;
};

export const computeTransitionWindowWithFStar = (
  position: number,
  commitCount: number,
): HistoryViewerWasmTransitionWindow | null => {
  const wasm = getReadyHistoryViewerModule();
  if (!wasm) return null;

  const outPtr = wasm._malloc(3 * 4);

  try {
    const outOffset = outPtr >>> 2;
    wasm._history_transition_window(
      Math.round(position * 1000),
      commitCount,
      outPtr,
    );

    return {
      lowerIndex: wasm.HEAPU32[outOffset] ?? 0,
      upperIndex: wasm.HEAPU32[outOffset + 1] ?? 0,
      localProgress: (wasm.HEAPU32[outOffset + 2] ?? 0) / 1000,
    };
  } finally {
    wasm._free(outPtr);
  }
};

export const computeMotionLinesWithFStar = (
  fromLines: readonly string[],
  toLines: readonly string[],
): HistoryViewerWasmMotionLine[] | null => {
  const wasm = getReadyHistoryViewerModule();
  if (!wasm) return null;

  const { fromIds, toIds } = internLineIds(fromLines, toLines);
  const dpWords = (fromIds.length + 1) * (toIds.length + 1);

  const fromBuf = allocU32(wasm, fromIds);
  const toBuf = allocU32(wasm, toIds);
  const dpPtr = wasm._malloc(dpWords * 4);
  const dpOffset = dpPtr >>> 2;
  wasm.HEAPU32.fill(0, dpOffset, dpOffset + dpWords);
  const statePtr = wasm._malloc(5 * 4);
  const stateOffset = statePtr >>> 2;
  wasm.HEAPU32.fill(0, stateOffset, stateOffset + 5);
  const outPtr = wasm._malloc(MOTION_STRIDE * 4);

  try {
    wasm._history_fill_lcs_table(
      fromBuf.ptr,
      fromIds.length,
      toBuf.ptr,
      toIds.length,
      dpPtr,
      dpWords,
    );

    const outOffset = outPtr >>> 2;
    const lines: HistoryViewerWasmMotionLine[] = [];

    while (
      wasm._history_motion_step(
        fromBuf.ptr,
        fromIds.length,
        toBuf.ptr,
        toIds.length,
        dpPtr,
        dpWords,
        statePtr,
        outPtr,
      ) === 0
    ) {
      lines.push({
        kind: decodeKind(wasm.HEAPU32[outOffset] ?? 0),
        fromIndex: decodeIndex(wasm.HEAPU32[outOffset + 1] ?? 0),
        toIndex: decodeIndex(wasm.HEAPU32[outOffset + 2] ?? 0),
        order: wasm.HEAPU32[outOffset + 3] ?? 0,
        staggerIndex: wasm.HEAPU32[outOffset + 4] ?? 0,
      });
    }

    return lines;
  } finally {
    wasm._free(fromBuf.ptr);
    wasm._free(toBuf.ptr);
    wasm._free(dpPtr);
    wasm._free(statePtr);
    wasm._free(outPtr);
  }
};

const encodeMotionLines = (
  lines: readonly HistoryViewerWasmMotionLine[],
): Uint32Array => {
  const encoded = new Uint32Array(Math.max(lines.length, 1) * MOTION_STRIDE);

  lines.forEach((line, index) => {
    const base = index * MOTION_STRIDE;
    encoded[base] =
      line.kind === "added"
        ? KIND_ADDED
        : line.kind === "removed"
          ? KIND_REMOVED
          : KIND_UNCHANGED;
    encoded[base + 1] = encodeIndex(line.fromIndex);
    encoded[base + 2] = encodeIndex(line.toIndex);
    encoded[base + 3] = line.order;
    encoded[base + 4] = line.staggerIndex;
  });

  return encoded;
};

export const computeStageLineCountWithFStar = (
  oldLineCount: number,
  newLineCount: number,
  motionLines: readonly HistoryViewerWasmMotionLine[],
): number | null => {
  const wasm = getReadyHistoryViewerModule();
  if (!wasm) return null;

  const encoded = encodeMotionLines(motionLines);
  const motionBuf = allocU32(wasm, encoded);

  try {
    return wasm._history_transition_stage_line_count(
      oldLineCount,
      newLineCount,
      motionBuf.ptr,
      motionLines.length,
    );
  } finally {
    wasm._free(motionBuf.ptr);
  }
};

export const computeViewportIndicatorsWithFStar = (
  scrollTop: number,
  clientHeight: number,
  rowHeight: number,
  motionLines: readonly HistoryViewerWasmMotionLine[],
): HistoryViewerWasmViewportIndicators | null => {
  const wasm = getReadyHistoryViewerModule();
  if (!wasm) return null;

  const encoded = encodeMotionLines(motionLines);
  const motionBuf = allocU32(wasm, encoded);

  try {
    const packed = wasm._history_transition_viewport_indicators(
      scrollTop,
      clientHeight,
      rowHeight,
      motionBuf.ptr,
      motionLines.length,
    );

    return {
      top: decodeIndicator(packed & 0b11),
      bottom: decodeIndicator((packed >> 2) & 0b11),
    };
  } finally {
    wasm._free(motionBuf.ptr);
  }
};

if (typeof window !== "undefined") {
  void primeHistoryViewerWasm();
}
